import { useEffect, useState } from "react";
import {
  ArrowRight, Check, Database, Brain, Workflow, BarChart3, Shield, Activity, Sparkles,
  Crosshair, GitBranch, MessagesSquare, CheckCircle2, Cog, TrendingUp, ChevronRight,
  Users, Mail, Megaphone, Briefcase, ScanSearch, Target, LineChart, Lightbulb,
  Building2,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import DemoDialog from "@/components/site/DemoDialog";
import DataFoundationRadial from "@/components/site/platform/DataFoundationRadial";
import DataFoundationRadialMobile from "@/components/site/platform/DataFoundationRadialMobile";
import DiagramWithSideLabels from "@/components/site/platform/DiagramWithSideLabels";
import ScrollProgress from "@/components/site/platform/ScrollProgress";
import IllustrativePill from "@/components/site/IllustrativePill";
import { IMAGES } from "@/lib/site";
import {
  HeroIntelligenceHub,
  DataFoundationDiagram,
  AutonomousIntelligenceMesh,
  SecurityShield,
  CTACrystalNetwork,
} from "@/components/platform/PlatformAssets";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

/* ────────── Constants ────────── */

const ARCHITECTURE = [
  { icon: Database, title: "Data Sources", lines: ["All systems.", "One foundation."] },
  { icon: Cog, title: "RealityOS Core", lines: ["Unified data layer.", "Built for real estate."] },
  { icon: Brain, title: "AI Intelligence Layer", lines: ["Advanced models.", "Actionable insights."] },
  { icon: Workflow, title: "Automation Layer", lines: ["Intelligent workflows.", "Operational scale."] },
  { icon: BarChart3, title: "Insights Layer", lines: ["Real-time analytics.", "Smarter decisions."] },
  { icon: TrendingUp, title: "Business Outcomes", lines: ["Measurable growth.", "Sustainable impact."] },
];

const INTELLIGENCE_MODULES = [
  { icon: Users, title: "Lead Intelligence", desc: "Identify, score, and prioritize the right opportunities." },
  { icon: Briefcase, title: "CRM Intelligence", desc: "Unify relationships and enrich every customer profile." },
  { icon: TrendingUp, title: "Sales Intelligence", desc: "Optimize pipelines and accelerate deal velocity." },
  { icon: Megaphone, title: "Marketing Intelligence", desc: "Measure impact and maximize campaign performance." },
  { icon: Activity, title: "Operational Intelligence", desc: "Streamline processes and improve team productivity." },
  { icon: ScanSearch, title: "Executive Intelligence", desc: "Real-time insights for confident decision making." },
];

const AUTONOMOUS_CAPS = [
  { icon: Activity, title: "Monitoring", desc: "Continuously monitor activity and performance." },
  { icon: Target, title: "Qualification", desc: "Evaluate and score leads with precision." },
  { icon: GitBranch, title: "Routing", desc: "Deliver leads to the right people." },
  { icon: LineChart, title: "Forecasting", desc: "Predict outcomes and pipeline performance." },
  { icon: Workflow, title: "Automation", desc: "Execute workflows and reduce manual work." },
  { icon: Lightbulb, title: "Recommendations", desc: "Deliver intelligent suggestions that drive results." },
];

const AUTOMATION_FLOW = [
  { icon: Crosshair, title: "Capture", desc: "Collect leads from any source." },
  { icon: Sparkles, title: "Qualify", desc: "AI scores and qualifies instantly." },
  { icon: GitBranch, title: "Route", desc: "Intelligent routing to the right team." },
  { icon: MessagesSquare, title: "Engage", desc: "Personalized outreach at scale." },
  { icon: CheckCircle2, title: "Convert", desc: "Close more deals with lead attribution." },
  { icon: BarChart3, title: "Analyze", desc: "Real-time performance and attribution." },
  { icon: Cog, title: "Optimize", desc: "Continuously learn and improve." },
];

const SECURITY_FEATURES = [
  { icon: Shield, title: "Security", desc: "Advanced encryption and threat protection." },
  { icon: Building2, title: "Governance", desc: "Role-based policies and controls." },
  { icon: CheckCircle2, title: "Compliance", desc: "SOC 2, GDPR, and industry standards." },
  { icon: Users, title: "Access Control", desc: "Granular permissions ensure the right access." },
  { icon: ScanSearch, title: "Auditability", desc: "Full audit trails, logs, and monitoring." },
  { icon: Activity, title: "Reliability", desc: "High reliability with continuous monitoring." },
];

/* Radial labels for the Unified Data Foundation hub — 8 nodes equally spaced around the hub */
const DATA_FOUNDATION_LABELS = [
  { icon: Database, title: "CRM Systems",            desc: "Connect and unify customer data.",          angle: 270 },
  { icon: Building2, title: "Property Systems",       desc: "Centralize property and asset data.",       angle: 315 },
  { icon: GitBranch, title: "Third-Party Integrations", desc: "One platform. Infinite possibilities.",   angle:   0 },
  { icon: Megaphone, title: "Marketing Platforms",    desc: "Unify campaigns and attribution.",          angle:  45 },
  { icon: Users,     title: "User Intelligence",      desc: "Investor, agent, and tenant behavior.",     angle:  90 },
  { icon: Mail,      title: "Lead Sources",           desc: "Capture every lead from every channel.",    angle: 135 },
  { icon: Workflow,  title: "Connected Systems",      desc: "Bring all operational systems together.",   angle: 180 },
  { icon: Cog,       title: "Data Sources",           desc: "All systems. One foundation.",              angle: 225 },
];

const INTEGRATIONS = [
  { name: "Salesforce", logo: "https://www.vectorlogo.zone/logos/salesforce/salesforce-icon.svg" },
  { name: "HubSpot", logo: "https://www.vectorlogo.zone/logos/hubspot/hubspot-icon.svg" },
  { name: "Microsoft", logo: "https://www.vectorlogo.zone/logos/microsoft/microsoft-icon.svg" },
  { name: "Google", logo: "https://www.vectorlogo.zone/logos/google/google-icon.svg" },
  { name: "Slack", logo: "https://www.vectorlogo.zone/logos/slack/slack-icon.svg" },
  { name: "Zapier", logo: "https://www.vectorlogo.zone/logos/zapier/zapier-icon.svg" },
];

const OUTCOMES = [
  { title: "Faster Response Times", desc: "Engage leads in seconds, not hours." },
  { title: "Unified Intelligence", desc: "One platform. One source of truth." },
  { title: "Improved Visibility", desc: "Real-time insights across the entire pipeline." },
  { title: "Scalable Growth", desc: "Built to scale with your business." },
  { title: "Reduced Manual Work", desc: "Automate repetitive tasks and save time." },
  { title: "Better Decisions", desc: "One platform. One source across your organization." },
];

const pipelineData = [
  { m: "Jan", v: 120, p: 110 }, { m: "Feb", v: 138, p: 122 },
  { m: "Mar", v: 152, p: 135 }, { m: "Apr", v: 148, p: 140 },
  { m: "May", v: 178, p: 152 }, { m: "Jun", v: 196, p: 168 },
  { m: "Jul", v: 215, p: 184 }, { m: "Aug", v: 232, p: 198 },
  { m: "Sep", v: 248, p: 212 }, { m: "Oct", v: 266, p: 230 },
  { m: "Nov", v: 284, p: 246 }, { m: "Dec", v: 302, p: 262 },
];

const AI_RECS = [
  { label: "High Priority Lead Follow-up", color: "text-sapphire-glow" },
  { label: "Deal at Risk", color: "text-amber-400" },
  { label: "Churn Risk Alert", color: "text-amber-400" },
  { label: "New Opportunity Detected", color: "text-emerald-400" },
];

/* ────────── Helpers ────────── */

function Eyebrow({ children }) {
  return (
    <div className="text-[10.5px] tracking-[0.24em] uppercase font-semibold text-sapphire-glow">
      {children}
    </div>
  );
}

function SectionCard({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative rounded-2xl border border-white/[0.07] ${className}`}
      style={{ background: "linear-gradient(180deg, rgba(20,28,48,0.55) 0%, rgba(13,18,32,0.85) 100%)" }}
    >
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/35 to-transparent" />
      {children}
    </div>
  );
}

/* ────────── Page ────────── */

export default function PlatformPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);

  return (
    <div className="min-h-screen bg-obsidian text-platinum" data-testid="platform-page">
      <Navbar onBookDemo={openDemo} />
      <ScrollProgress />

      <main className="pt-[110px] lg:pt-[124px]">
        {/* ─────── HERO ─────── */}
        <section className="relative pb-12 lg:pb-16 overflow-x-clip" data-testid="platform-hero">
          <div className="absolute inset-0 bg-grid opacity-[0.18]" aria-hidden />
          <div
            className="absolute -top-32 right-0 w-[720px] h-[720px] pointer-events-none"
            style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.18), transparent)" }}
            aria-hidden
          />

          <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
            <div className="grid lg:grid-cols-[1fr_1.15fr_1.25fr] gap-6 lg:gap-8 items-center">
              <div>
                <Eyebrow>ESTHERA REALITYOS PLATFORM</Eyebrow>
                <h1 className="font-display mt-5 text-platinum text-[40px] lg:text-[52px] xl:text-[56px] leading-[1.04] tracking-[-0.018em]">
                  The Intelligence Layer
                  <br />
                  for{" "}
                  <span className="text-sapphire-glow" style={{ textShadow: "0 0 40px rgba(96,165,250,0.28)" }}>
                    Modern Real Estate
                  </span>
                </h1>
                <p className="mt-6 text-[15.5px] text-platinum/70 leading-[1.7] max-w-[460px]">
                  Unify your data. Automate operations. Empower every team with
                  intelligence across the entire real estate lifecycle.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <button
                    onClick={openDemo}
                    data-testid="platform-hero-book-demo"
                    className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                  >
                    Book a Demo
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <a
                    href="#capabilities"
                    data-testid="platform-hero-explore"
                    className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                  >
                    Explore Capabilities
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Center — Hero Intelligence Hub */}
              <div data-testid="hero-intelligence-hub">
                <HeroIntelligenceHub />
              </div>

              {/* Right — Platform Overview Dashboard */}
              <div data-testid="platform-overview-dashboard">
                <SectionCard className="overflow-hidden relative">
                  <IllustrativePill position="top-3 right-3" z="z-30" />
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="text-[12px] font-medium text-platinum">Platform Overview</div>
                      <div className="text-[10px] text-platinum/45 tracking-wide">This Month ▾</div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                      {[
                        { l: "Total Pipeline Value", v: "$238.7M", d: "↑ 18.6%" },
                        { l: "Active Deals", v: "237", d: "↑ 12.1%" },
                        { l: "New Leads", v: "842", d: "↑ 21.4%" },
                        { l: "Win Rate", v: "18.6%", d: "↑ 2.3%" },
                      ].map((k, i) => (
                        <div key={i} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5">
                          <div className="text-[9.5px] tracking-wide text-platinum/55 uppercase">{k.l}</div>
                          <div className="mt-1 font-display text-[18px] text-platinum leading-none">{k.v}</div>
                          <div className="mt-1 text-[9.5px] text-emerald-400">{k.d} vs last month</div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] text-platinum/65">Pipeline Trend</div>
                        <div className="text-[10px] text-platinum/45">— This Year — Last Year</div>
                      </div>
                      <div className="h-[120px] mt-1" style={{ minHeight: 120 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={pipelineData}>
                            <defs>
                              <linearGradient id="pp" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.45} />
                                <stop offset="100%" stopColor="#60A5FA" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                            <XAxis dataKey="m" stroke="rgba(216,222,233,0.35)" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                            <YAxis hide />
                            <Tooltip contentStyle={{ background: "#0D1B3D", border: "1px solid rgba(96,165,250,0.35)", color: "#D8DEE9", fontSize: 11, borderRadius: 6 }} />
                            <Area type="monotone" dataKey="p" stroke="rgba(245,158,11,0.55)" strokeWidth={1.2} fill="none" strokeDasharray="3 2" />
                            <Area type="monotone" dataKey="v" stroke="#60A5FA" strokeWidth={2} fill="url(#pp)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] text-platinum/65 tracking-wide">AI Recommendations</div>
                          <a href="#" className="text-[9.5px] text-sapphire-glow">View all</a>
                        </div>
                        <ul className="mt-1.5 space-y-1">
                          {AI_RECS.map((r, i) => (
                            <li key={i} className="flex items-center gap-1.5 text-[10.5px] text-platinum/80">
                              <span className={`w-1 h-1 rounded-full ${r.color === "text-emerald-400" ? "bg-emerald-400" : r.color === "text-amber-400" ? "bg-amber-400" : "bg-sapphire-glow"}`} />
                              {r.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="grid grid-rows-2 gap-2">
                        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5">
                          <div className="text-[9.5px] tracking-wide text-platinum/55 uppercase">Tasks Due Today</div>
                          <div className="font-display text-[18px] text-platinum leading-none mt-0.5">28</div>
                          <div className="text-[9.5px] text-emerald-400">↑ 18% vs yesterday</div>
                        </div>
                        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5 flex items-center justify-between">
                          <div>
                            <div className="text-[9.5px] tracking-wide text-platinum/55 uppercase">Lead Conv. Rate</div>
                            <div className="font-display text-[16px] text-platinum leading-none mt-0.5">24.5%</div>
                            <div className="text-[9.5px] text-emerald-400">↑ 4.3%</div>
                          </div>
                          <div className="relative w-9 h-9">
                            <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
                              <circle cx="18" cy="18" r="14" stroke="rgba(96,165,250,0.18)" strokeWidth="3" fill="none" />
                              <circle cx="18" cy="18" r="14" stroke="#60A5FA" strokeWidth="3" fill="none"
                                strokeDasharray={`${(24.5 / 100) * (2 * Math.PI * 14)} 99`} strokeLinecap="round" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </SectionCard>
              </div>
            </div>
          </div>
        </section>

        {/* ─────── ARCHITECTURE FLOW ─────── */}
        <section className="relative pb-14 lg:pb-16" data-testid="architecture-flow">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <SectionCard>
              <div className="p-7 lg:p-8">
                <div className="grid lg:grid-cols-[1fr_3.6fr] gap-6 lg:gap-8 items-start lg:items-center">
                  <div>
                    <Eyebrow>THE ESTHERA REALITYOS ARCHITECTURE</Eyebrow>
                    <div className="mt-3 text-[13px] text-platinum/55 leading-[1.6]">
                      A unified platform.<br />Endless possibilities.
                    </div>
                  </div>
                  <div className="flex items-stretch gap-2 overflow-x-auto pb-2 -mx-2 px-2">
                    {ARCHITECTURE.map((a, i) => (
                      <div key={a.title} className="flex items-center shrink-0">
                        <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-4 py-3 min-w-[150px]">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-md border border-sapphire-glow/30 bg-sapphire/[0.10] inline-flex items-center justify-center">
                              <a.icon className="w-3.5 h-3.5 text-sapphire-glow" />
                            </div>
                            <div className="text-[12px] font-medium text-platinum tracking-tight leading-tight">{a.title}</div>
                          </div>
                          <div className="mt-2 text-[10.5px] text-platinum/55 leading-[1.45]">
                            {a.lines.map((l) => (
                              <div key={l}>{l}</div>
                            ))}
                          </div>
                        </div>
                        {i < ARCHITECTURE.length - 1 && (
                          <ChevronRight className="mx-1 w-4 h-4 text-sapphire-glow/70 shrink-0" strokeWidth={2.5} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        </section>

        {/* ─────── UNIFIED DATA FOUNDATION (full-width, text-left / radial diagram right) ─────── */}
        <section id="capabilities" className="relative pb-16 lg:pb-24">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <div className="grid lg:grid-cols-[1fr_1.35fr] gap-10 lg:gap-16 items-center" data-testid="unified-data-foundation">
              <div className="lg:pr-6">
                <Eyebrow>UNIFIED DATA FOUNDATION</Eyebrow>
                <h2 className="font-display mt-4 text-platinum text-[32px] sm:text-[38px] lg:text-[44px] leading-[1.06] tracking-[-0.018em]">
                  Connect everything.<br />
                  <span className="text-sapphire-glow" style={{ textShadow: "0 0 38px rgba(96,165,250,0.28)" }}>See everything.</span>
                </h2>
                <p className="mt-5 text-[15px] text-platinum/65 leading-[1.7] max-w-[480px]">
                  One unified data layer that brings every system, every source, and every signal into a single intelligence core — purpose-built for real estate.
                </p>
              </div>
              <div>
                <DataFoundationRadial labels={DATA_FOUNDATION_LABELS} />
                <DataFoundationRadialMobile labels={DATA_FOUNDATION_LABELS} />
              </div>
            </div>
          </div>
        </section>

        {/* ─────── INTELLIGENCE MODULES (full-width card) ─────── */}
        <section className="relative pb-16 lg:pb-20">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <SectionCard testId="intelligence-modules">
              <div className="p-7 sm:p-9 lg:p-10">
                <div className="grid lg:grid-cols-[1fr_2fr] gap-8 lg:gap-12 items-start">
                  <div>
                    <Eyebrow>INTELLIGENCE MODULES</Eyebrow>
                    <h2 className="font-display mt-4 text-platinum text-[24px] sm:text-[28px] lg:text-[32px] leading-[1.08] tracking-[-0.015em]">
                      Built-in intelligence for every function.
                    </h2>
                    <p className="mt-4 text-[14px] text-platinum/60 leading-[1.65] max-w-[360px]">
                      Specialized modules across leads, CRM, sales, marketing, operations, and the executive view.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                    {INTELLIGENCE_MODULES.map((m, i) => (
                      <div key={m.title} className="rounded-xl border border-white/[0.07] bg-white/[0.015] p-4 sm:p-5 hover:border-sapphire-glow/30 transition-colors" data-testid={`module-${i}`}>
                        <div className="w-9 h-9 rounded-md border border-sapphire-glow/30 bg-sapphire/[0.10] inline-flex items-center justify-center">
                          <m.icon className="w-4.5 h-4.5 text-sapphire-glow" />
                        </div>
                        <div className="mt-3.5 font-display text-platinum text-[15px] leading-tight">{m.title}</div>
                        <div className="mt-2 text-[11.5px] text-platinum/55 leading-[1.5]">{m.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        </section>

        {/* ─────── AUTONOMOUS INTELLIGENCE LAYER (full-width, diagram-left / text-right) ─────── */}
        <section className="relative pb-16 lg:pb-24">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <div className="grid lg:grid-cols-[1.35fr_1fr] gap-10 lg:gap-16 items-center" data-testid="autonomous-intelligence">
              <div className="order-2 lg:order-1">
                <DiagramWithSideLabels
                  image={IMAGES.platformAutonomous}
                  leftLabels={AUTONOMOUS_CAPS.slice(0, 3)}
                  rightLabels={AUTONOMOUS_CAPS.slice(3, 6)}
                  bloomColor="rgba(96,165,250,0.32)"
                  imgScale={2.2}
                />
              </div>
              <div className="lg:pl-6 order-1 lg:order-2">
                <Eyebrow>AUTONOMOUS INTELLIGENCE LAYER</Eyebrow>
                <h2 className="font-display mt-4 text-platinum text-[32px] sm:text-[38px] lg:text-[44px] leading-[1.06] tracking-[-0.018em]">
                  Specialized intelligence,<br />
                  <span className="text-sapphire-glow" style={{ textShadow: "0 0 38px rgba(96,165,250,0.28)" }}>working for you.</span>
                </h2>
                <p className="mt-5 text-[15px] text-platinum/65 leading-[1.7] max-w-[480px]">
                  AI layer that continuously monitors, qualifies, and orchestrates every signal of your real estate operations — for measurable, repeatable results.
                </p>
                <p className="mt-6 text-[12px] text-platinum/45 leading-[1.5] max-w-[440px]">
                  Powered by advanced AI models, machine learning, and real-time data signals to autonomously optimize your entire revenue engine.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ─────── AUTOMATION LAYER (full-width card) ─────── */}
        <section className="relative pb-16 lg:pb-20">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <SectionCard testId="automation-layer">
              <div className="p-7 sm:p-9 lg:p-10">
                <div className="grid lg:grid-cols-[1fr_2.6fr] gap-8 lg:gap-12 items-center">
                  <div>
                    <Eyebrow>AUTOMATION LAYER</Eyebrow>
                    <h2 className="font-display mt-4 text-platinum text-[24px] sm:text-[28px] lg:text-[32px] leading-[1.08] tracking-[-0.015em]">
                      Intelligent workflows that move your business forward.
                    </h2>
                    <p className="mt-4 text-[14px] text-platinum/60 leading-[1.65] max-w-[360px]">
                      An end-to-end pipeline of automation — capture, qualify, route, engage, convert, analyze, optimize — running quietly in the background.
                    </p>
                  </div>
                  <div className="relative">
                    {/* Connection line — sits BEHIND icons */}
                    <div
                      className="pointer-events-none absolute left-[5%] right-[5%] top-[24px] h-px hidden md:block z-0"
                      style={{
                        background:
                          "linear-gradient(90deg, rgba(245,158,11,0) 0%, rgba(245,158,11,0.55) 12%, rgba(251,191,36,0.85) 50%, rgba(245,158,11,0.55) 88%, rgba(245,158,11,0) 100%)",
                        boxShadow: "0 0 10px rgba(245,158,11,0.45)",
                      }}
                      aria-hidden
                    />
                    {/* Traveling intelligence pulse */}
                    <div
                      className="pointer-events-none absolute top-[20px] hidden md:block z-[1]"
                      style={{ width: "90%", left: "5%" }}
                      aria-hidden
                    >
                      <div className="relative h-2">
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full pulse-travel"
                          style={{
                            background:
                              "radial-gradient(closest-side, #FBBF24 0%, rgba(245,158,11,0.6) 50%, transparent 100%)",
                            boxShadow: "0 0 14px rgba(251,191,36,0.85)",
                          }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-y-7 gap-x-2 items-start relative z-10">
                      {AUTOMATION_FLOW.map((f, i) => (
                        <div key={f.title} className="relative flex flex-col items-center text-center">
                          {/* Continuous gold arrow between stages (desktop only) */}
                          {i < AUTOMATION_FLOW.length - 1 && (
                            <div className="hidden md:block absolute top-[18px] -right-2 w-4 h-4 overflow-hidden z-[2]">
                              <ChevronRight
                                className="absolute inset-0 m-auto w-3.5 h-3.5 text-amber-400 arrow-flow"
                                style={{ animationDelay: `${i * 0.5}s` }}
                                strokeWidth={2.5}
                              />
                            </div>
                          )}
                          <div
                            className="w-12 h-12 rounded-full inline-flex items-center justify-center icon-breathe"
                            style={{
                              animationDelay: `${i * 0.4}s`,
                              background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.22), #0E1422 70%)",
                              border: "1px solid rgba(96,165,250,0.42)",
                              boxShadow: "0 0 16px -2px rgba(96,165,250,0.45)",
                            }}
                          >
                            <f.icon className="w-5 h-5 text-sapphire-glow" />
                          </div>
                          <div className="font-display mt-3 text-platinum text-[12.5px] leading-tight">{f.title}</div>
                          <div className="mt-1 text-[10px] text-platinum/45 leading-snug px-1">{f.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        </section>

        {/* ─────── ENTERPRISE SECURITY (full-width, text-left / diagram-right) ─────── */}
        <section className="relative pb-16 lg:pb-24">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <div className="grid lg:grid-cols-[1fr_1.35fr] gap-10 lg:gap-16 items-center" data-testid="enterprise-security">
              <div className="lg:pr-6">
                <Eyebrow>ENTERPRISE SECURITY</Eyebrow>
                <h2 className="font-display mt-4 text-platinum text-[32px] sm:text-[38px] lg:text-[44px] leading-[1.06] tracking-[-0.018em]">
                  Built with enterprise-grade<br />
                  <span className="text-sapphire-glow" style={{ textShadow: "0 0 38px rgba(96,165,250,0.28)" }}>security at the core.</span>
                </h2>
                <p className="mt-5 text-[15px] text-platinum/65 leading-[1.7] max-w-[480px]">
                  RealityOS is built with enterprise-grade security and compliance at the core — so you can innovate with confidence.
                </p>
                <p className="mt-6 text-[12px] text-platinum/45 leading-[1.5] max-w-[440px]">
                  Enterprise-grade security. Built for trust. Designed for scale.
                </p>
              </div>
              <DiagramWithSideLabels
                image={IMAGES.platformSecurity}
                leftLabels={SECURITY_FEATURES.slice(0, 3).map((f, i) => ({ ...f, icon: [Shield, CheckCircle2, ScanSearch][i] }))}
                rightLabels={SECURITY_FEATURES.slice(3, 6).map((f, i) => ({ ...f, icon: [Building2, Users, Activity][i] }))}
                bloomColor="rgba(37,99,235,0.32)"
                imgScale={2.0}
              />
            </div>
          </div>
        </section>

        {/* ─────── SEAMLESS INTEGRATIONS (full-width row) ─────── */}
        <section className="relative pb-14 lg:pb-16">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <SectionCard testId="seamless-integrations">
              <div className="p-7 sm:p-9 lg:p-10">
                <Eyebrow>SEAMLESS INTEGRATIONS</Eyebrow>
                <h3 className="font-display mt-3 text-platinum text-[24px] sm:text-[28px] lg:text-[32px] leading-tight tracking-[-0.015em]">
                  Connect your favorite tools.
                </h3>
                <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5">
                  {INTEGRATIONS.map((it) => (
                    <div
                      key={it.name}
                      className="rounded-xl border border-white/[0.07] bg-white/[0.02] hover:border-sapphire-glow/30 transition-colors p-6 sm:p-7 flex flex-col items-center gap-4"
                    >
                      <div className="h-16 sm:h-20 flex items-center justify-center">
                        <img
                          src={it.logo}
                          alt={it.name}
                          className="max-h-16 sm:max-h-20 max-w-[80%] object-contain"
                          draggable="false"
                        />
                      </div>
                      <div className="text-[14px] sm:text-[15px] text-platinum/85 font-medium tracking-tight">
                        {it.name}
                      </div>
                    </div>
                  ))}
                  <div className="rounded-xl border border-dashed border-white/[0.14] flex flex-col items-center justify-center text-platinum/55 p-6 sm:p-7 gap-3">
                    <div className="text-[26px] sm:text-[30px] leading-none">+</div>
                    <div className="text-[13px] sm:text-[14px] text-platinum/55">And many more</div>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        </section>

        {/* ─────── MEASURABLE OUTCOMES ─────── */}
        <section className="relative pb-14 lg:pb-16">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <SectionCard testId="measurable-outcomes">
              <div className="p-6 sm:p-7 lg:p-8">
                <Eyebrow>MEASURABLE OUTCOMES</Eyebrow>
                <h3 className="font-display mt-2 text-platinum text-[22px] leading-tight">
                  Built for impact. Designed for growth.
                </h3>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
                  {OUTCOMES.map((o) => (
                    <div key={o.title} className="flex gap-3">
                      <div className="w-5 h-5 rounded-full border border-amber-400/55 inline-flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-amber-400" strokeWidth={3} />
                      </div>
                      <div>
                        <div className="text-[13px] font-medium text-platinum leading-tight">{o.title}</div>
                        <div className="mt-1 text-[11px] text-platinum/55 leading-snug">{o.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          </div>
        </section>

        {/* ─────── FINAL CTA ─────── */}
        <section className="relative pb-20 lg:pb-24" data-testid="platform-final-cta">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <SectionCard>
              <div className="relative p-10 sm:p-14 lg:p-20 overflow-hidden">
                {/* Crystal watermark — full-section ambient backdrop.
                     mix-blend-mode: screen drops the asset's dark background to transparent,
                     reduced opacity keeps it as a low-key watermark behind the text.
                     Anchored RIGHT so the asset's embedded left-side feature cards & title
                     fall off-screen, only the crystal + star network shows. */}
                <div className="absolute inset-0 pointer-events-none">
                  <img
                    src={IMAGES.ctaCrystalNetwork}
                    alt=""
                    aria-hidden="true"
                    className="absolute breathe-glow"
                    style={{
                      mixBlendMode: "screen",
                      opacity: 0.55,
                      right: "-6%",
                      top: "50%",
                      transform: "translateY(-50%)",
                      height: "125%",
                      width: "auto",
                      objectFit: "cover",
                    }}
                    draggable="false"
                  />

                  {/* Left-half dark scrim — masks any remaining embedded asset content
                       behind the text so the headline/description stay legible. */}
                  <div
                    className="absolute inset-y-0 left-0"
                    style={{
                      width: "82%",
                      background:
                        "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,1) 70%, rgba(10,15,26,0.9) 85%, rgba(10,15,26,0.35) 95%, rgba(10,15,26,0) 100%)",
                    }}
                  />

                  {/* Top scrim — fades asset's embedded title text at the top */}
                  <div
                    className="absolute inset-x-0 top-0"
                    style={{
                      height: "22%",
                      background:
                        "linear-gradient(180deg, rgba(10,15,26,0.96) 0%, rgba(10,15,26,0.6) 60%, rgba(10,15,26,0) 100%)",
                    }}
                  />

                  {/* Bottom scrim — hides asset's embedded CTA buttons + tagline */}
                  <div
                    className="absolute inset-x-0 bottom-0"
                    style={{
                      height: "26%",
                      background:
                        "linear-gradient(0deg, rgba(10,15,26,0.98) 0%, rgba(10,15,26,0.85) 45%, rgba(10,15,26,0.35) 80%, rgba(10,15,26,0) 100%)",
                    }}
                  />

                  {/* Outer vignette — softens edges into the section card */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "radial-gradient(ellipse 90% 110% at 65% 50%, transparent 25%, rgba(10,15,26,0.45) 75%, rgba(10,15,26,0.85) 100%)",
                    }}
                  />

                  {/* Sapphire bloom anchored right to give the crystal lift */}
                  <div
                    className="absolute -right-[10%] top-1/2 -translate-y-1/2 w-[55%] h-[140%]"
                    style={{
                      background:
                        "radial-gradient(closest-side, rgba(37,99,235,0.32), rgba(37,99,235,0.08) 55%, transparent 80%)",
                      filter: "blur(60px)",
                    }}
                  />
                </div>

                {/* Text content — anchored left, max-width keeps it readable on top of watermark */}
                <div className="relative z-10 max-w-[640px]">
                  <Eyebrow>READY TO TRANSFORM</Eyebrow>
                  <h3 className="font-display mt-4 text-platinum text-[30px] sm:text-[38px] lg:text-[48px] leading-[1.04] tracking-[-0.018em]">
                    Ready to <span className="text-sapphire-glow" style={{ textShadow: "0 0 40px rgba(96,165,250,0.45)" }}>transform</span><br />
                    your real estate operations?
                  </h3>
                  <p className="mt-6 text-[14.5px] sm:text-[15.5px] text-platinum/72 leading-[1.7] max-w-[560px]">
                    See how Esthera RealityOS can unify your data, automate your workflows,
                    and drive intelligent growth across your entire organization.
                  </p>
                  <div className="mt-9 flex flex-wrap items-center gap-3">
                    <button
                      onClick={openDemo}
                      data-testid="platform-cta-book-demo"
                      className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                    >
                      Book a Demo
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <a
                      href="#capabilities"
                      data-testid="platform-cta-explore"
                      className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                    >
                      Explore Capabilities
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        </section>
      </main>

      <Footer />
      <DemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
